import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const FirmHeader = ({ firm }) => {
  const theme = useTheme();
  const gapGreen = theme.custom.colors.gapGreen;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        pb: 1,
        borderBottom: '0.6px dashed',
        borderColor: 'divider',
        mb: 0.5,
        background: '#F7F7F7',
        borderTopLeftRadius: (theme) => theme.spacing(1),
        borderTopRightRadius: (theme) => theme.spacing(1),
        p: 1

      }}
    >
      <Box sx={{ minWidth: '25%', flex: 1 }}>
        <Typography variant="p3" color="text.secondary">Firm Name</Typography>
        <Typography variant="labelM">{firm.firm_name}</Typography>
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="p3" color="text.secondary">Manufacturer</Typography>
        <Typography variant="labelM">{firm.manufacturer_name}</Typography>
      </Box>
      <Box sx={{ flexShrink: 0, minWidth: 80, textAlign: 'right' }}>
        <Typography variant="p3" color="text.secondary">Total</Typography>
        <Typography variant="labelM" sx={{ color: gapGreen, whiteSpace: 'nowrap' }}>
          {firm.manufacturer_amount}
        </Typography>
      </Box>
    </Box>
  );
};

const BeneficiaryTableHeader = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1 }}>
    <Typography variant="p3" color="text.secondary" sx={{ minWidth: '25%', flex: 1 }}>Beneficiary Name</Typography>
    <Typography variant="p3" color="text.secondary" sx={{ flex: 1 }}>SAP Code</Typography>
    <Typography variant="p3" color="text.secondary" sx={{ flexShrink: 0, minWidth: 80, textAlign: 'right' }}>Amount</Typography>
  </Box>
);

const BeneficiaryRow = ({ row }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', px: 1 }}>
    <Typography variant="labelM" sx={{ minWidth: '25%', flex: 1 }}>{row.beneficiary_name}</Typography>
    <Typography variant="labelM" sx={{ flex: 1, whiteSpace: 'nowrap' }}>{row.entity_info}</Typography>
    <Typography variant="labelM" sx={{ flexShrink: 0, minWidth: 80, textAlign: 'right', whiteSpace: 'nowrap' }}>{row.entity_amount}</Typography>
  </Box>
);

const QuarterCard = ({ quarter, defaultExpanded }) => {
  const hasDetail = quarter.manufacturer_wise_firm_gap_list?.length > 0;
  const [expanded, setExpanded] = useState(defaultExpanded && hasDetail);

  return (
    <Box sx={{ bgcolor: 'background.default', borderRadius: 2, p: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box
        onClick={() => hasDetail && setExpanded((v) => !v)}
        sx={{ display: 'flex', alignItems: 'center', cursor: hasDetail ? 'pointer' : 'default', width: '100%' }}
      >
        <Typography variant="labelM" sx={{ flex: 1 }}>
          {quarter.quarter_label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, justifyContent: 'flex-end' }}>
          <Typography variant="labelM" sx={{ whiteSpace: 'nowrap' }}>{quarter.quarter_amount}</Typography>
          {hasDetail && (expanded
            ? <KeyboardArrowUpIcon fontSize="small" sx={{ color: 'primary.main' }} />
            : <KeyboardArrowDownIcon fontSize="small" sx={{ color: 'primary.main' }} />
          )}
        </Box>
      </Box>

      {/* Expanded firms */}
      {expanded && quarter.manufacturer_wise_firm_gap_list?.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {quarter.manufacturer_wise_firm_gap_list.map((firm, fi) => (
            <Box
              key={fi}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                pb: 1,
              }}
            >
              <FirmHeader firm={firm} />
              {firm.entity_wise_gap_list?.length > 0 && (
                <>
                  <BeneficiaryTableHeader />
                  {firm.entity_wise_gap_list.map((row, ri) => (
                    <BeneficiaryRow key={ri} row={row} />
                  ))}
                </>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export const GrossGAPSection = ({ grossGapSummary , subject_to_tds }) => {
  const theme = useTheme();
  const { gapGreen, gapRed } = theme.custom.colors;

  if (!grossGapSummary) return null;
  const { title, total, quarters: quarterGroups } = grossGapSummary;
  const allQuarters = quarterGroups?.flatMap((g) => g.quarters ?? []) ?? [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="labelD">{title}</Typography>

      {allQuarters.map((quarter, idx) => (
        <QuarterCard key={quarter.quarter_label ?? idx} quarter={quarter} defaultExpanded={idx === 0} />
      ))}
      <Box
        sx={{
          background: alpha(gapGreen, 0.08),
          borderRadius: 2,
          p: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="labelD">{total?.label || 'Total Gross GAP'}</Typography>
        <Typography variant="labelD" sx={{ whiteSpace: 'nowrap' }}>{total?.value}*</Typography>
      </Box>
      
      {subject_to_tds && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <WarningAmberIcon sx={{ fontSize: 16, color: gapRed, flexShrink: 0 }} />
          <Typography variant="p3" sx={{ color: gapRed, whiteSpace: 'nowrap' }}>
            {subject_to_tds}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default QuarterCard;
